import { AppError, type RestResponse } from "@artisancode/types";
import {
  MessageCircle,
  Plug,
  RefreshCw,
  Star,
  Trash2,
  Unplug,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ConnectionRow } from "@/components/shared/connection-row";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  useRemoveWhatsappDevice,
  useSetPrimaryWhatsappDevice,
  useWhatsappLogout,
  useWhatsappReconnect,
} from "@/hooks/use-whatsapp";
import { formatPhone } from "@/lib/utils";

import type { WhatsappDevice } from "@artisancode/api-types";

function errorMessage(error: unknown, fallback: string) {
  if (error instanceof AppError && (error.httpCode ?? 0) < 500) {
    const backendMessage = (error.data as RestResponse | undefined)?.message;
    return backendMessage || error.message;
  }
  return fallback;
}

export function WhatsappDeviceRow({
  device,
  logout,
  reconnect,
  setPrimary,
  remove,
  onConnect,
}: {
  device: WhatsappDevice;
  logout: ReturnType<typeof useWhatsappLogout>;
  reconnect: ReturnType<typeof useWhatsappReconnect>;
  setPrimary: ReturnType<typeof useSetPrimaryWhatsappDevice>;
  remove: ReturnType<typeof useRemoveWhatsappDevice>;
  onConnect: (deviceId: string) => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handleLogout() {
    try {
      await logout.mutateAsync(device.id);
      toast.success("WhatsApp berhasil diputuskan.");
    } catch (error) {
      toast.error(errorMessage(error, "Gagal memutuskan WhatsApp."));
    }
  }

  async function handleReconnect() {
    try {
      await reconnect.mutateAsync(device.id);
      toast.success("Mencoba menyambungkan ulang...");
    } catch (error) {
      toast.error(errorMessage(error, "Gagal menyambungkan ulang WhatsApp."));
    }
  }

  async function handleSetPrimary() {
    try {
      await setPrimary.mutateAsync(device.id);
      toast.success("Device utama diperbarui.");
    } catch (error) {
      toast.error(errorMessage(error, "Gagal menjadikan device ini utama."));
    }
  }

  async function handleRemove() {
    try {
      await remove.mutateAsync(device.id);
      toast.success("Device dihapus.");
      setConfirmOpen(false);
    } catch (error) {
      toast.error(errorMessage(error, "Gagal menghapus device."));
    }
  }

  const phoneLabel = device.jid
    ? `+62 ${formatPhone((device.jid.split("@")[0] ?? "").replace(/^62/, ""))}`
    : null;

  return (
    <>
      <ConnectionRow
        icon={MessageCircle}
        title={device.name || phoneLabel || "WhatsApp — belum terhubung"}
        subtitle={
          device.name ? (phoneLabel ?? device.id) : device.jid || device.id
        }
        badge={
          <>
            {device.isPrimary && <Badge className="shrink-0">Utama</Badge>}
            <Badge
              variant={device.isLoggedIn ? "default" : "secondary"}
              className="shrink-0"
            >
              {device.isLoggedIn ? "Terhubung" : "Belum Terhubung"}
            </Badge>
          </>
        }
        actions={
          <>
            {!device.isLoggedIn && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Hubungkan"
                onClick={() => onConnect(device.id)}
              >
                <Plug className="h-3.5 w-3.5" />
              </Button>
            )}
            {device.isLoggedIn && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Sambungkan ulang"
                onClick={handleReconnect}
                disabled={reconnect.isPending}
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            )}
            {device.isLoggedIn && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Putuskan"
                onClick={handleLogout}
                disabled={logout.isPending}
              >
                <Unplug className="h-3.5 w-3.5 text-destructive" />
              </Button>
            )}
            {!device.isPrimary && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Jadikan utama"
                onClick={handleSetPrimary}
                disabled={setPrimary.isPending}
              >
                <Star className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Hapus"
              onClick={() => setConfirmOpen(true)}
            >
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </>
        }
      />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Hapus device WhatsApp ini?"
        confirmLabel="Hapus"
        onConfirm={handleRemove}
        isConfirming={remove.isPending}
      />
    </>
  );
}
